import React, { useEffect, useMemo, useState } from "react";

import CloseIcon from "@mui/icons-material/Close";
import { Box, IconButton, Modal, Stack, Typography } from "@mui/material";
import { VisibilityLock } from "assets/material-custom-icons";
import { useFilePreviewHooks } from "components/old-data-box-components/File-preview/use-file-preview-hooks";
import { FileActionsPrompt } from "components/Prompts";
import { useDealIdSelector, useUserRoleSelector } from "store";
import { isRoleAnalyst } from "utils/user";

import { IFile } from "../../../store/types";

interface FileViewerProps {
  file?: IFile | Partial<IFile>;
  isOpen: boolean;
  onClose: React.MouseEventHandler<HTMLButtonElement>;
  hideActions?: boolean;
}

export const FileViewer: React.FC<FileViewerProps> = ({ file, isOpen, onClose }) => {
  if (!file) return null;
  const [actionsAnchorEl, setActionsAnchorEl] = useState<HTMLElement | null>(null);
  const dealId = useDealIdSelector();
  const role = useUserRoleSelector();
  const { openShareDialog, onDelete, onMoveTo, onDownload, onAddTask, onSetPrivate, onCategorize, onLookThrough } =
    useFilePreviewHooks(file as IFile);

  useEffect(() => {
    if (!isOpen) {
      setActionsAnchorEl(null);
    }
  }, [isOpen]);

  const actionsOptions = useMemo(() => {
    if (!dealId) {
      return {
        onDownload,
        ...(isRoleAnalyst({ role, preventTV: true }) && { onDelete })
      };
    }

    const baseOptions = {
      onShare: openShareDialog,
      onMoveTo,
      onDownload,
      onAddTask,
      ...(onSetPrivate && {
        onSetPrivate: { action: onSetPrivate, title: file?.is_private ? "Set to Public" : "Set to Private" }
      }),
      ...(isRoleAnalyst({ role, preventTV: true }) && { onDelete })
    };

    if (!file?.deal_id && !file?.section_id && file?.document_id) {
      return {
        onShare: openShareDialog,
        onMoveTo,
        onDownload,
        onAddTask,
        ...(onSetPrivate && {
          onSetPrivate: { action: onSetPrivate, title: file?.is_private ? "Set to Public" : "Set to Private" }
        }),
        onDelete: isRoleAnalyst({ role, preventTV: true }) && file?.deal_id ? onDelete : undefined
      };
    }

    return baseOptions;
  }, [
    dealId,
    file,
    onAddTask,
    onCategorize,
    onDelete,
    onDownload,
    onLookThrough,
    onMoveTo,
    onSetPrivate,
    openShareDialog,
    role
  ]);

  const renderContent = useMemo(() => {
    if (file?.type === "pdf") {
      return <object data={file.file_url} type="application/pdf" width="100%" height="100%" />;
    }
    if (file?.type === "png" || file?.type === "jpeg" || file?.type === "jpg") {
      return (
        <img
          src={file?.file_url}
          alt={file?.name}
          style={{
            padding: "20px",
            maxWidth: "100%",
            maxHeight: "100%"
          }}
        />
      );
    } else return;
  }, [file]);

  return (
    <Modal onClose={onClose} open={isOpen}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          outline: "none",
          minWidth: "80vw",
          marginRight: "10px",
          borderRadius: "10px",
          overflow: "hidden"
        }}
      >
        <Stack
          sx={{
            height: "50px",
            width: "100%",
            padding: "0 22px 0 30px",
            flexShrink: 0,
            borderTopLeftRadius: "10px",
            borderTopRightRadius: "10px",
            backgroundColor: "#edf1f9",
            color: "#0a3142",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between"
          }}
        >
          <Typography
            sx={{
              fontSize: "18px",
              color: "#0a3142"
            }}
          >
            {file?.name}
          </Typography>
          <Stack flexDirection="row" alignItems="center" justifyContent="center" gap="15px">
            {file?.is_private && <VisibilityLock sx={{ fill: "#BAC7CE" }} />}
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Stack>
        </Stack>
        <FileActionsPrompt
          anchorEl={actionsAnchorEl}
          open={Boolean(actionsAnchorEl)}
          onClose={() => setActionsAnchorEl(null)}
          options={actionsOptions}
        />
        <Stack
          sx={{
            backgroundColor: "#ffffff",
            height: "90vh",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "center"
          }}
        >
          {renderContent}
        </Stack>
      </Box>
    </Modal>
  );
};
